/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

export interface BrokerLike {
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

/**
 * The display name of an agency member, falling back to their e-mail.
 *
 * @param members - The agency's members.
 * @param email - The member being named.
 * @returns The member's full name, or the e-mail when unknown.
 */
export function memberName(members: BrokerLike[], email: string): string {
  const member = members.find(
    (m) => (m.email ?? '').toLowerCase() === email.toLowerCase(),
  );
  if (!member) return email;
  return `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim() || email;
}

/**
 * "Jorge Gabriel (2), Lucas Venezian (1)" — who holds how many.
 *
 * A bare list of names beside a total cannot be read: "3 abertos com
 * Jorge, Lucas" leaves the reader unable to tell one colleague holding
 * three from three colleagues holding one each, which is the difference
 * between a crowded client and a duplicate about to be made.
 *
 * Busiest first, so the name worth talking to comes first. Every name
 * carries its number, including "(1)": the point is that the reader
 * never has to infer a split, and an unmarked name next to a marked one
 * is exactly the inference this replaces.
 *
 * @param members - The agency's members, for resolving names.
 * @param counts - How many open attendances each broker holds, by e-mail.
 * @returns The rendered breakdown, or the empty string when nobody holds any.
 */
export function brokerBreakdown(
  members: BrokerLike[],
  counts: Record<string, number>,
): string {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([email, n]) => `${memberName(members, email)} (${n})`)
    .join(', ');
}

/**
 * Total attendances across a breakdown.
 *
 * @param counts - How many each broker holds.
 * @returns The sum.
 */
export function totalOf(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}
